import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import {
  AppError,
  ToastNotification,
  ErrorContextState,
  ToastSeverity,
  ErrorType,
} from '../types/errors';

// Action types for the error reducer
type ErrorAction =
  | { type: 'ADD_ERROR'; payload: AppError }
  | { type: 'REMOVE_ERROR'; payload: string }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'ADD_TOAST'; payload: ToastNotification }
  | {
      type: 'UPDATE_TOAST';
      payload: { id: string; updates: Partial<ToastNotification> };
    }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'CLEAR_TOASTS' };

// Context interface
interface ErrorContextValue extends ErrorContextState {
  addError: (error: Partial<AppError>) => void;
  removeError: (errorId: string) => void;
  clearErrors: () => void;
  showToast: (
    message: string,
    severity?: ToastSeverity,
    options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
  ) => string;
  updateToast: (toastId: string, updates: Partial<Omit<ToastNotification, 'id'>>) => void;
  showSuccessToast: (
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
  ) => string;
  showErrorToast: (
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
  ) => string;
  showWarningToast: (
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
  ) => string;
  showInfoToast: (
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
  ) => string;
  removeToast: (toastId: string) => void;
  clearToasts: () => void;
}

// Initial state
const initialState: ErrorContextState = {
  errors: [],
  toasts: [],
};

// Reducer function
function errorReducer(state: ErrorContextState, action: ErrorAction): ErrorContextState {
  switch (action.type) {
    case 'ADD_ERROR':
      return {
        ...state,
        errors: [...state.errors, action.payload],
      };
    case 'REMOVE_ERROR':
      return {
        ...state,
        errors: state.errors.filter((error) => error.id !== action.payload),
      };
    case 'CLEAR_ERRORS':
      return {
        ...state,
        errors: [],
      };
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload],
      };
    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === action.payload.id ? { ...toast, ...action.payload.updates } : toast
        ),
      };
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter((toast) => toast.id !== action.payload),
      };
    case 'CLEAR_TOASTS':
      return {
        ...state,
        toasts: [],
      };
    default:
      return state;
  }
}

// Create the context
const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

// Provider component
interface ErrorProviderProps {
  children: ReactNode;
  maxErrors?: number;
  maxToasts?: number;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({
  children,
  maxErrors = 10,
  maxToasts = 5,
}) => {
  const [state, dispatch] = useReducer(errorReducer, initialState);

  // Generate unique IDs
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add error function
  const addError = useCallback(
    (errorData: Partial<AppError>) => {
      const error: AppError = {
        id: errorData.id || generateId(),
        type: errorData.type || ErrorType.UNKNOWN_ERROR,
        message: errorData.message || 'An unknown error occurred',
        timestamp: errorData.timestamp || new Date(),
        severity: errorData.severity || ToastSeverity.ERROR,
        dismissible: errorData.dismissible ?? true,
        autoHideDuration: errorData.autoHideDuration ?? 6000,
        ...errorData,
      };

      dispatch({ type: 'ADD_ERROR', payload: error });

      // Auto-create toast for error
      const toast: ToastNotification = {
        id: error.id,
        message: error.message,
        severity: error.severity,
        dismissible: error.dismissible,
        autoHideDuration: error.autoHideDuration,
        actionLabel: error.actionLabel,
        onAction: error.onAction,
      };

      dispatch({ type: 'ADD_TOAST', payload: toast });

      // Limit the number of errors stored
      if (state.errors.length >= maxErrors) {
        const oldestError = state.errors[0];
        dispatch({ type: 'REMOVE_ERROR', payload: oldestError.id });
      }
    },
    [state.errors, maxErrors, generateId]
  );

  // Remove error function
  const removeError = useCallback((errorId: string) => {
    dispatch({ type: 'REMOVE_ERROR', payload: errorId });
  }, []);

  // Clear errors function
  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
  }, []);

  // Show toast function
  const showToast = useCallback(
    (
      message: string,
      severity: ToastSeverity = ToastSeverity.INFO,
      options: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>> = {}
    ): string => {
      const toastId = generateId();
      const toast: ToastNotification = {
        id: toastId,
        message,
        severity,
        dismissible: options.dismissible ?? true,
        autoHideDuration:
          options.autoHideDuration ?? (severity === ToastSeverity.ERROR ? 8000 : 4000),
        actionLabel: options.actionLabel,
        onAction: options.onAction,
      };

      dispatch({ type: 'ADD_TOAST', payload: toast });

      // Limit the number of toasts displayed
      if (state.toasts.length >= maxToasts) {
        const oldestToast = state.toasts[0];
        dispatch({ type: 'REMOVE_TOAST', payload: oldestToast.id });
      }

      return toastId;
    },
    [state.toasts, maxToasts, generateId]
  );

  // Update toast function
  const updateToast = useCallback(
    (toastId: string, updates: Partial<Omit<ToastNotification, 'id'>>) => {
      dispatch({ type: 'UPDATE_TOAST', payload: { id: toastId, updates } });
    },
    []
  );

  // Convenience functions for different toast types
  const showSuccessToast = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
    ): string => showToast(message, ToastSeverity.SUCCESS, options),
    [showToast]
  );

  const showErrorToast = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
    ): string => showToast(message, ToastSeverity.ERROR, options),
    [showToast]
  );

  const showWarningToast = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
    ): string => showToast(message, ToastSeverity.WARNING, options),
    [showToast]
  );

  const showInfoToast = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastNotification, 'id' | 'message' | 'severity'>>
    ): string => showToast(message, ToastSeverity.INFO, options),
    [showToast]
  );

  // Remove toast function
  const removeToast = useCallback((toastId: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: toastId });
  }, []);

  // Clear toasts function
  const clearToasts = useCallback(() => {
    dispatch({ type: 'CLEAR_TOASTS' });
  }, []);

  const contextValue: ErrorContextValue = {
    ...state,
    addError,
    removeError,
    clearErrors,
    showToast,
    updateToast,
    showSuccessToast,
    showErrorToast,
    showWarningToast,
    showInfoToast,
    removeToast,
    clearToasts,
  };

  return <ErrorContext.Provider value={contextValue}>{children}</ErrorContext.Provider>;
};

// Custom hook to use the error context
// eslint-disable-next-line react-refresh/only-export-components
export const useError = (): ErrorContextValue => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

// Export the context for advanced usage
export { ErrorContext };
export type { ErrorContextValue };
